<script setup lang="ts">
import { useStorage } from '@vueuse/core';
import type { RouteLocationRaw } from 'vue-router';
import type { AgentOnPage } from '@@/shared/agent/api-types';
import type { IadAppRoute } from '~/application';

const props = defineProps<{
  agent: AgentOnPage
}>();

const isMounted = useMounted();

// BACK LINK
const returnTo = useStorage(`agent-${props.agent.userName}`, ``);
const backLink = computed<IadAppRoute<`find-real-estate-agent`> | RouteLocationRaw>(() => {
  if (isMounted.value && returnTo.value) return returnTo.value;
  return {
    name: 'find-real-estate-agent',
  };
});

// SHARE
const { t } = useI18n();

const title = computed(() => {
  const { place, postcode } = props.agent.location;
  const translationKey = props.agent.displayGender === `female`
    ? `agent.header.agent_in_female`
    : `agent.header.agent_in_male`;
  return t(translationKey, { place, postcode });
});

// VALUATION LINK
const valuationLink = computed(() => {
  if (!isMounted.value) return { };
  const estimateLink: IadAppRoute<`real-estate-agent-userName-valuation`> = {
    name: `real-estate-agent-userName-valuation`,
    params: { userName: props.agent.userName },
  };
  return {
    to: estimateLink,
  };
});
</script>

<template>
  <div class="contents">
    <IadWrapper>
      <div class="flex gap-m justify-between py-s">
        <IadBtn
          variant="outlined"
          icon
          :to="backLink"
        >
          <Icon name="iad-icon:arrow-left" />
        </IadBtn>
        <AgentShare
          :title
          :agent
        />
      </div>
    </IadWrapper>
    <IadWrapper
      :padded="false"
    >
      <div class="flex flex-col md:flex-row items-center gap-l">
        <AgentAvatarLarge
          :agent
          variant="agent-page"
        />
        <div class="flex-1 px-m flex flex-col gap-m">
          <div>
            <IadTitle
              variant="large"
              tag="h1"
              class="text-secondary"
              :text="agent.fullName"
            />
            <IadTitle
              tag="h2"
              variant="small-semibold"
              class="pt-2xs"
              :text="title"
            />
          </div>
          <IadReviewSummary
            v-if="agent.reviewsCount"
            :average-rating="agent.reviewsRatingAverage"
            :reviews-count="agent.reviewsCount"
          />
          <AgentLanguages :agent />
          <div class="flex flex-wrap gap-m items-baseline">
            <p
              class="font-semibold text-xl hidden md:block"
              v-text="$t(`agent.header.valuation_question`)"
            />
            <IadBtn
              size="large"
              class="w-full md:w-auto"
              v-bind="valuationLink"
              :text="$t(`agent.header.valuation_cta`)"
            />
          </div>
        </div>
      </div>
    </IadWrapper>
  </div>
</template>
